import { toPersian, formatPersianDateTime } from '../../utils/persianNumber';
import { formatEstimatedMinutes } from '../ui/EstimatedTimeSlider';
import { useCompanyDisplay } from '../../utils/companyDisplay';

const PRINT_FONT = `Pinar, Peynar, "پینار", Vazirmatn, Tahoma, sans-serif`;

const PRINT_STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;600;700;800&display=swap');
@font-face {
  font-family: Pinar;
  src: local("Pinar"), local("Pinar Regular"), local("Peynar"), local("پینار");
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: Pinar;
  src: local("Pinar Bold"), local("Peynar Bold"), local("پینار بولد");
  font-weight: 700;
  font-style: normal;
}
@media print {
  body * { visibility: hidden !important; }
  #daily-report-print-sheet, #daily-report-print-sheet * { visibility: visible !important; }
  #daily-report-print-sheet {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    font-family: ${PRINT_FONT} !important;
  }
  #daily-report-print-sheet * { font-family: inherit !important; }
  @page { size: A4 portrait; margin: 12mm; }
}
#daily-report-print-sheet {
  position: fixed;
  left: -9999px;
  top: 0;
  width: 210mm;
  min-height: 297mm;
  background: #fff;
  color: #111;
  direction: rtl;
  font-family: ${PRINT_FONT};
  box-sizing: border-box;
}
#daily-report-print-sheet * { font-family: inherit; }
.drp-brand-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #1B3068 0%, #233A7A 45%, #29AAE1 100%);
  color: #fff;
  border-radius: 0;
}
.drp-brand-logo-wrap {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.drp-brand-logo { width: 34px; height: 34px; border-radius: 8px; object-fit: contain; }
.drp-brand-text { flex: 1; text-align: center; }
.drp-brand-org { font-size: 16px; font-weight: 800; margin: 0 0 3px; letter-spacing: -0.2px; }
.drp-brand-sub { font-size: 11px; margin: 0; opacity: 0.9; }
.drp-brand-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.22);
  white-space: nowrap;
}
.drp-body { padding: 12mm 12mm 10mm; }
.drp-title-wrap {
  text-align: center;
  margin: 0 0 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid #1B3068;
}
.drp-title { font-size: 17px; font-weight: 800; margin: 0; color: #0f172a; }
.drp-meta {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
  font-size: 12px;
}
.drp-approval-approved { background: #f0fdf4; border-color: #86efac; }
.drp-approval-pending { background: #fffbeb; border-color: #fde68a; }
.drp-approval-waiting { background: #f8fafc; border-color: #cbd5e1; }
.drp-not-approved {
  font-size: 10px;
  color: #94a3b8;
  text-align: center;
  margin-top: 8px;
  font-style: italic;
}
.drp-meta-box {
  border: 1px solid #B7C9EC;
  border-radius: 8px;
  padding: 8px 10px;
  background: #EDF3FC;
}
.drp-meta-label { font-size: 10px; color: #64748b; margin-bottom: 4px; }
.drp-meta-value { font-weight: 700; color: #0f172a; }
.drp-parts { display: flex; flex-direction: column; gap: 12px; margin-bottom: 20px; }
.drp-part-box {
  border: 1.5px solid #1B3068;
  border-radius: 8px;
  overflow: hidden;
  page-break-inside: avoid;
}
.drp-part-head {
  background: #D9E4F7;
  color: #1B3068;
  font-size: 12px;
  font-weight: 800;
  padding: 7px 10px;
  border-bottom: 1px solid #B7C9EC;
}
.drp-part-body { padding: 8px 10px 10px; }
.drp-entry { margin-bottom: 12px; }
.drp-entry:last-child { margin-bottom: 0; }
.drp-entry-title { font-size: 12px; font-weight: 700; color: #0f172a; margin-bottom: 6px; }
.drp-fields { width: 100%; border-collapse: collapse; font-size: 11px; margin: 0 0 8px; }
.drp-fields th, .drp-fields td {
  border: 1px solid #94a3b8;
  padding: 5px 8px;
  text-align: right;
  vertical-align: middle;
}
.drp-fields th { background: #EDF3FC; color: #1B3068; font-weight: 700; white-space: nowrap; }
.drp-fields td { color: #0f172a; }
.drp-desc-label { font-size: 10px; color: #64748b; margin: 2px 0 4px; }
.drp-dotted-box {
  line-height: 24px;
  font-size: 12px;
  color: #0f172a;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  background-image: repeating-linear-gradient(
    to bottom,
    transparent 0,
    transparent 23px,
    #64748b 23px,
    #64748b 24px
  );
  padding: 0 4px;
  min-height: 24px;
}
.drp-empty { text-align: center; color: #64748b; padding: 16px; font-size: 12px; }
.drp-signatures {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
  margin-top: 24px;
  page-break-inside: avoid;
}
.drp-sign-box {
  border: 1px solid #64748b;
  border-radius: 8px;
  padding: 10px;
  min-height: 88px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.drp-sign-label { font-size: 11px; font-weight: 700; color: #0f172a; text-align: center; }
.drp-sign-line { border-bottom: 1px dashed #94a3b8; height: 46px; margin-top: 8px; }
.drp-sign-img { height: 46px; margin-top: 8px; display: flex; align-items: center; justify-content: center; }
.drp-sign-img img { max-height: 44px; max-width: 100%; object-fit: contain; }
.drp-footnote {
  font-size: 9px;
  color: #94a3b8;
  text-align: center;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}
`;

/** Split stored fields for print/display (legacy description lines supported). */
export function parseDailyReportItemFields(it) {
  let company = (it.companyName || '').trim();
  let phoneNumber = (it.phoneNumber || '').trim();
  let fullName = (it.fullName || '').trim();
  let cost = (it.cost || '').trim();
  const descLines = [];

  const invoiceId = it.invoiceId ?? it.invoiceTicketId;
  if (invoiceId && !cost) {
    const status = it.invoiceStatusLabel || '';
    cost = status ? `فاکتور #${invoiceId} (${status})` : `فاکتور #${invoiceId}`;
  }

  for (const line of (it.description || '').split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith('شرکت:')) {
      if (!company) company = trimmed.slice(5).trim();
    } else if (trimmed.startsWith('شماره تماس:')) {
      if (!phoneNumber) phoneNumber = trimmed.slice(11).trim();
    } else if (trimmed.startsWith('نام و نام خانوادگی:')) {
      if (!fullName) fullName = trimmed.slice(19).trim();
    } else if (trimmed.startsWith('مبلغ:')) {
      if (!cost) cost = trimmed.slice(5).trim();
    } else {
      descLines.push(line);
    }
  }

  return {
    company: company || '—',
    phoneNumber: phoneNumber || '—',
    fullName: fullName || '—',
    cost: cost ? toPersian(cost) : '—',
    description: descLines.join('\n').trim() || '—',
  };
}

function DottedDescription({ text }) {
  const raw = (text && text !== '—') ? String(text) : '';
  return <div className="drp-dotted-box">{raw || '\u00a0'}</div>;
}

function fieldColumns(fields, estimatedTime) {
  const cols = [];
  const has = (v) => v && v !== '—';
  if (estimatedTime != null && estimatedTime !== '') {
    cols.push({ key: 'time', label: 'زمان', value: formatEstimatedMinutes(estimatedTime) });
  }
  if (has(fields.company)) cols.push({ key: 'company', label: 'نام شرکت', value: fields.company });
  if (has(fields.phoneNumber)) cols.push({ key: 'phone', label: 'شماره تماس', value: fields.phoneNumber });
  if (has(fields.fullName)) cols.push({ key: 'name', label: 'نام کامل', value: fields.fullName });
  if (has(fields.cost)) cols.push({ key: 'cost', label: 'فاکتور / مالی', value: fields.cost });
  return cols;
}

function FieldsTable({ columns }) {
  if (!columns.length) return null;
  return (
    <table className="drp-fields">
      <thead>
        <tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
      </thead>
      <tbody>
        <tr>{columns.map((c) => <td key={c.key}>{c.value || '—'}</td>)}</tr>
      </tbody>
    </table>
  );
}

function buildPrintSections(parts, items, partLabel) {
  const sections = [];
  const seen = new Set();
  for (const p of parts || []) {
    if (p == null || p.item_id == null) continue;
    const id = String(p.item_id);
    if (seen.has(id)) continue;
    seen.add(id);
    const name = p.parent_id ? `↳ ${p.name}` : (p.name || partLabel?.(p.item_id) || '—');
    sections.push({
      id,
      name,
      items: [],
      need_company_name: !!p.need_company_name,
      need_phone_number: !!p.need_phone_number,
      need_full_name: !!p.need_full_name,
      need_cost: !!p.need_cost,
    });
  }
  const byId = Object.fromEntries(sections.map((s) => [s.id, s]));
  const leftover = {
    id: '_unassigned',
    name: 'سایر',
    items: [],
    need_company_name: false,
    need_phone_number: false,
    need_full_name: false,
    need_cost: false,
  };
  for (const it of items || []) {
    const pid = it.partId == null || it.partId === '' ? '' : String(it.partId);
    if (pid && byId[pid]) byId[pid].items.push(it);
    else leftover.items.push(it);
  }
  if (leftover.items.length) sections.push(leftover);
  return sections.filter((s) => s.items.length > 0);
}

export default function DailyReportPrintForm({
  dateLabel,
  employeeName,
  departmentName,
  items,
  parts = [],
  partLabel,
  expertSignatureUrl,
  managerSignatureUrl,
  managerName,
  approvalStatusLabel,
  approvedAt,
}) {
  const companyLabel = useCompanyDisplay();
  const logoSrc = `${window.location.origin}/pejvak-mark.svg`;
  const isApproved = approvalStatusLabel === 'تایید شده';
  const sections = buildPrintSections(parts, items, partLabel);

  return (
    <>
      <style>{PRINT_STYLE}</style>
      <div id="daily-report-print-sheet" aria-hidden="true">
        <div className="drp-brand-bar">
          <div className="drp-brand-logo-wrap">
            <img src={logoSrc} alt="" className="drp-brand-logo" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          </div>
          <div className="drp-brand-text">
            <p className="drp-brand-org">پژواک رایانه نقش جهان</p>
            <p className="drp-brand-sub">سامانه CRM — مدیریت پشتیبانی و ارتباط با مشتری</p>
          </div>
          <div className="drp-brand-badge">فرم رسمی</div>
        </div>

        <div className="drp-body">
          <div className="drp-title-wrap">
            <h1 className="drp-title">فرم گزارش کار روزانه</h1>
          </div>

          <div className="drp-meta">
            <div className="drp-meta-box">
              <div className="drp-meta-label">تاریخ</div>
              <div className="drp-meta-value">{dateLabel}</div>
            </div>
            <div className="drp-meta-box">
              <div className="drp-meta-label">نام و نام خانوادگی</div>
              <div className="drp-meta-value">{employeeName || '—'}</div>
            </div>
            <div className="drp-meta-box">
              <div className="drp-meta-label">نام دپارتمان</div>
              <div className="drp-meta-value">{departmentName || '—'}</div>
            </div>
            <div className={`drp-meta-box ${isApproved ? 'drp-approval-approved' : approvalStatusLabel === 'نیاز به تایید مجدد' ? 'drp-approval-pending' : 'drp-approval-waiting'}`}>
              <div className="drp-meta-label">وضعیت تایید</div>
              <div className="drp-meta-value">
                {approvalStatusLabel || '—'}
                {isApproved && approvedAt ? ` · ${formatPersianDateTime(approvedAt)}` : ''}
              </div>
            </div>
          </div>

          <div className="drp-parts">
            {sections.length === 0 ? (
              <div className="drp-empty">موردی برای چاپ ثبت نشده است</div>
            ) : sections.map((section) => (
              <div key={section.id} className="drp-part-box">
                <div className="drp-part-head">{section.name}</div>
                <div className="drp-part-body">
                  {section.items.map((it, i) => {
                    const parsed = parseDailyReportItemFields(it);
                    const fields = parsed.company && parsed.company !== '—' ? { ...parsed, company: companyLabel(parsed.company) } : parsed;
                    const columns = fieldColumns(fields, it.estimatedTime);
                    const hasDesc = fields.description && fields.description !== '—';
                    return (
                      <div key={it.id || i} className="drp-entry">
                        <div className="drp-entry-title">{it.title || '—'}</div>
                        <FieldsTable columns={columns} />
                        {hasDesc && (
                          <>
                            <div className="drp-desc-label">توضیحات</div>
                            <DottedDescription text={fields.description} />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="drp-signatures">
            <div className="drp-sign-box">
              <div className="drp-sign-label">امضای کارشناس</div>
              {expertSignatureUrl ? (
                <div className="drp-sign-img">
                  <img src={expertSignatureUrl} alt="" />
                </div>
              ) : (
                <div className="drp-sign-line" />
              )}
            </div>
            <div className="drp-sign-box">
              <div className="drp-sign-label">
                {managerName ? `امضای مدیر دپارتمان (${managerName})` : 'امضای مدیر دپارتمان'}
              </div>
              {managerSignatureUrl ? (
                <div className="drp-sign-img">
                  <img src={managerSignatureUrl} alt="" />
                </div>
              ) : (
                <>
                  <div className="drp-sign-line" />
                  {!isApproved && (
                    <div className="drp-not-approved">تایید نشده</div>
                  )}
                </>
              )}
            </div>
            <div className="drp-sign-box">
              <div className="drp-sign-label">امضای مدیر منابع انسانی</div>
              <div className="drp-sign-line" />
            </div>
          </div>

          <p className="drp-footnote">crm.pejvakrayane.com — صادرشده از سامانه CRM پژواک رایانه</p>
        </div>
      </div>
    </>
  );
}
